import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, User, Eye, EyeOff, MessageCircle, ShieldCheck, Loader2, Phone, ArrowLeft } from 'lucide-react';
import { API_BASE_URL } from './config';

const Login = ({ onWhatsAppLogin, onEmailLogin, onEmailRegister, onGuestLogin, onForgotPassword, error }) => {
    const { t } = useTranslation();
    const [isRegistering, setIsRegistering] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [localError, setLocalError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // WhatsApp OTP flow
    const [waStep, setWaStep] = useState('idle'); // 'idle' | 'phone' | 'code'
    const [waPhone, setWaPhone] = useState('');
    const [waCode, setWaCode] = useState('');
    const [waLoading, setWaLoading] = useState(false);
    const [waError, setWaError] = useState('');

    const handleWaSendCode = async () => {
        setWaError('');
        if (!waPhone.trim()) { setWaError(t('enter_phone_number')); return; }
        setWaLoading(true);
        try {
            const resp = await fetch(`${API_BASE_URL}/whatsapp_otp_send.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: waPhone.trim() })
            });
            const data = await resp.json();
            if (resp.ok && data.success) {
                setWaStep('code');
            } else {
                setWaError(data.message || t('error_sending_code'));
            }
        } catch {
            setWaError(t('server_error'));
        } finally {
            setWaLoading(false);
        }
    };

    const handleWaVerify = async () => {
        setWaError('');
        if (!waCode.trim()) { setWaError(t('enter_code')); return; }
        setWaLoading(true);
        try {
            const resp = await fetch(`${API_BASE_URL}/whatsapp_otp_verify.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: waPhone.trim(), code: waCode.trim() })
            });
            const data = await resp.json();
            if (resp.ok && data.success) {
                onWhatsAppLogin(data.token);
            } else {
                setWaError(data.message || t('invalid_code'));
            }
        } catch {
            setWaError(t('server_error'));
        } finally {
            setWaLoading(false);
        }
    };

    const resetWaFlow = () => { setWaStep('idle'); setWaPhone(''); setWaCode(''); setWaError(''); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        setSuccessMessage('');

        if (!username || !password || (isRegistering && !name)) {
            setLocalError(t('fill_all_fields'));
            return;
        }

        setIsLoading(true);
        try {
            if (isRegistering) {
                await onEmailRegister(username, password, name);
            } else {
                await onEmailLogin(username, password);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGuestLogin = async () => {
        setLocalError('');
        setIsLoading(true);
        try {
            await onGuestLogin();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="tw-flex tw-justify-center tw-items-center tw-min-h-screen tw-p-4">
            <div className="tw-w-full tw-max-w-sm">
                {/* Card */}
                <div className="tw-rounded-3xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-8">

                    {/* Heading */}
                    <div className="tw-text-center tw-mb-7">
                        <h1 className="tw-text-2xl tw-font-bold tw-text-muted-fore tw-mb-1">{t('login_title')}</h1>
                        <p className="tw-text-sm tw-text-muted">{t('login_method_prompt')}</p>
                    </div>

                    {/* Username/Password form */}
                    <form onSubmit={handleSubmit} className="tw-space-y-3 tw-mb-4">
                        {/* Username */}
                        <div className="tw-relative">
                            <User size={15} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted tw-pointer-events-none" />
                            <input
                                type="text"
                                placeholder={t('username_label')}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                disabled={isLoading}
                                className="tw-w-full tw-pl-9 tw-pr-4 tw-py-3 tw-rounded-xl tw-bg-surface-3 tw-border tw-border-surface-border tw-text-sm tw-text-muted-fore tw-placeholder-muted tw-outline-none focus:tw-border-accent/60 focus:tw-ring-1 focus:tw-ring-accent/30 tw-transition-all tw-duration-150 disabled:tw-opacity-50"
                            />
                        </div>

                        {/* Name (register only) */}
                        {isRegistering && (
                            <div className="tw-relative">
                                <User size={15} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted tw-pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder={t('name_label')}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    className="tw-w-full tw-pl-9 tw-pr-4 tw-py-3 tw-rounded-xl tw-bg-surface-3 tw-border tw-border-surface-border tw-text-sm tw-text-muted-fore tw-placeholder-muted tw-outline-none focus:tw-border-accent/60 focus:tw-ring-1 focus:tw-ring-accent/30 tw-transition-all tw-duration-150 disabled:tw-opacity-50"
                                />
                            </div>
                        )}

                        {/* Password */}
                        <div className="tw-relative">
                            <Lock size={15} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted tw-pointer-events-none" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder={t('password_label')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={isLoading}
                                className="tw-w-full tw-pl-9 tw-pr-10 tw-py-3 tw-rounded-xl tw-bg-surface-3 tw-border tw-border-surface-border tw-text-sm tw-text-muted-fore tw-placeholder-muted tw-outline-none focus:tw-border-accent/60 focus:tw-ring-1 focus:tw-ring-accent/30 tw-transition-all tw-duration-150 disabled:tw-opacity-50"
                            />
                            <button
                                type="button"
                                aria-label={t('toggle_password_visibility')}
                                onClick={() => setShowPassword(!showPassword)}
                                className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted tw-hover:tw-text-muted-fore tw-transition-colors"
                            >
                                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                            {isRegistering && (
                                <p className="tw-text-xs tw-text-muted tw-mt-1 tw-ml-1">{t('password_helper')}</p>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="tw-w-full tw-py-3 tw-rounded-xl tw-bg-accent tw-text-white tw-font-semibold tw-text-sm tw-transition-all tw-duration-200 tw-hover:tw-brightness-110 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-1"
                        >
                            {isLoading ? <Loader2 size={16} className="tw-animate-spin" /> : null}
                            {isLoading ? null : isRegistering ? t('create_account_btn') : t('login_btn')}
                        </button>

                        {/* Forgot password */}
                        {!isRegistering && (
                            <button
                                type="button"
                                onClick={() => { if (onForgotPassword) onForgotPassword(username); }}
                                disabled={isLoading}
                                className="tw-w-full tw-text-xs tw-text-muted tw-hover:tw-text-accent-glow tw-transition-colors tw-text-center tw-py-1"
                            >
                                {t('forgot_password')}
                            </button>
                        )}

                        {/* Toggle register/login */}
                        <button
                            type="button"
                            onClick={() => { setIsRegistering(!isRegistering); setLocalError(''); setSuccessMessage(''); }}
                            disabled={isLoading}
                            className="tw-w-full tw-text-xs tw-text-muted tw-hover:tw-text-muted-fore tw-transition-colors tw-text-center tw-py-1"
                        >
                            {isRegistering ? t('switch_to_login') : t('switch_to_signup')}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="tw-flex tw-items-center tw-gap-3 tw-my-4">
                        <div className="tw-flex-1 tw-h-px tw-bg-surface-border" />
                        <span className="tw-text-xs tw-text-muted">{t('or_divider')}</span>
                        <div className="tw-flex-1 tw-h-px tw-bg-surface-border" />
                    </div>

                    {/* Continue as Guest */}
                    <div className="tw-space-y-3">
                        <button
                            onClick={handleGuestLogin}
                            disabled={isLoading}
                            title={t('guest_login_tooltip')}
                            className="tw-w-full tw-py-3 tw-rounded-xl tw-border tw-border-surface-border tw-bg-surface-3/50 tw-text-sm tw-text-muted-fore tw-font-medium tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all tw-duration-200 tw-hover:tw-bg-surface-3 tw-hover:tw-border-accent/40 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            <User size={15} className="tw-text-muted" />
                            {t('continue_as_guest')}
                        </button>
                        <p className="tw-text-xs tw-text-muted tw-text-center">{t('guest_login_helper')}</p>

                        {/* WhatsApp OTP flow */}
                        {waStep === 'idle' && (
                            <button
                                onClick={() => { setWaStep('phone'); setWaError(''); }}
                                disabled={isLoading}
                                className="tw-w-full tw-py-3 tw-rounded-xl tw-bg-[#25d366] tw-text-white tw-font-semibold tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all tw-duration-200 tw-hover:tw-brightness-110 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                <MessageCircle size={15} />
                                {t('whatsapp_login_btn')}
                            </button>
                        )}

                        {waStep === 'phone' && (
                            <div className="tw-space-y-2">
                                <div className="tw-relative">
                                    <Phone size={15} className="tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted tw-pointer-events-none" />
                                    <input
                                        type="tel"
                                        placeholder={t('enter_phone_number')}
                                        value={waPhone}
                                        onChange={(e) => setWaPhone(e.target.value)}
                                        disabled={waLoading}
                                        autoFocus
                                        className="tw-w-full tw-pl-9 tw-pr-4 tw-py-3 tw-rounded-xl tw-bg-surface-3 tw-border tw-border-surface-border tw-text-sm tw-text-muted-fore tw-placeholder-muted tw-outline-none focus:tw-border-[#25d366]/60 focus:tw-ring-1 focus:tw-ring-[#25d366]/30 tw-transition-all tw-duration-150 disabled:tw-opacity-50"
                                    />
                                </div>
                                {waError && <p className="tw-text-xs tw-text-red-400">{waError}</p>}
                                <button
                                    onClick={handleWaSendCode}
                                    disabled={waLoading}
                                    className="tw-w-full tw-py-3 tw-rounded-xl tw-bg-[#25d366] tw-text-white tw-font-semibold tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all tw-duration-200 tw-hover:tw-brightness-110 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                >
                                    {waLoading ? <Loader2 size={15} className="tw-animate-spin" /> : <MessageCircle size={15} />}
                                    {t('send_code')}
                                </button>
                                <button
                                    onClick={resetWaFlow}
                                    disabled={waLoading}
                                    className="tw-w-full tw-text-xs tw-text-muted tw-hover:tw-text-muted-fore tw-transition-colors tw-text-center tw-py-1"
                                >
                                    {t('cancel_btn')}
                                </button>
                            </div>
                        )}

                        {waStep === 'code' && (
                            <div className="tw-space-y-2">
                                <p className="tw-text-xs tw-text-muted tw-text-center">{t('whatsapp_code_sent_to')} {waPhone}</p>
                                <div className="tw-relative">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder={t('enter_code')}
                                        value={waCode}
                                        onChange={(e) => setWaCode(e.target.value.replace(/\D/g, ''))}
                                        disabled={waLoading}
                                        autoFocus
                                        className="tw-w-full tw-px-4 tw-py-3 tw-rounded-xl tw-bg-surface-3 tw-border tw-border-surface-border tw-text-sm tw-text-muted-fore tw-placeholder-muted tw-outline-none focus:tw-border-[#25d366]/60 focus:tw-ring-1 focus:tw-ring-[#25d366]/30 tw-transition-all tw-duration-150 disabled:tw-opacity-50 tw-text-center tw-tracking-widest tw-font-mono tw-text-base"
                                    />
                                </div>
                                {waError && <p className="tw-text-xs tw-text-red-400">{waError}</p>}
                                <button
                                    onClick={handleWaVerify}
                                    disabled={waLoading}
                                    className="tw-w-full tw-py-3 tw-rounded-xl tw-bg-[#25d366] tw-text-white tw-font-semibold tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all tw-duration-200 tw-hover:tw-brightness-110 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                >
                                    {waLoading ? <Loader2 size={15} className="tw-animate-spin" /> : null}
                                    {t('verify')}
                                </button>
                                <button
                                    onClick={() => { setWaStep('phone'); setWaCode(''); setWaError(''); }}
                                    disabled={waLoading}
                                    className="tw-w-full tw-text-xs tw-text-muted tw-hover:tw-text-muted-fore tw-transition-colors tw-text-center tw-py-1 tw-flex tw-items-center tw-justify-center tw-gap-1"
                                >
                                    <ArrowLeft size={12} />
                                    {t('back')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Security notice */}
                    <p className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-xs tw-text-muted tw-mt-5">
                        <ShieldCheck size={13} className="tw-text-accent-glow" />
                        {t('secure_login_indicator')}
                    </p>

                    {/* Errors / Success */}
                    {(error || localError) && (
                        <div className="tw-mt-4 tw-px-4 tw-py-3 tw-rounded-xl tw-bg-red-900/20 tw-border tw-border-red-500/30 tw-text-xs tw-text-red-300">
                            {error || localError}
                        </div>
                    )}
                    {successMessage && (
                        <div className="tw-mt-4 tw-px-4 tw-py-3 tw-rounded-xl tw-bg-emerald-900/20 tw-border tw-border-emerald-500/30 tw-text-xs tw-text-emerald-300">
                            {successMessage}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;
